<template>
  <!-- DUPLICATED from packages/frontend for the studio (intentional — see
       docs/superpowers/specs/2026-06-01-battleplan-authoring-tool-design.md §5).
       Keep rendering in sync with the frontend original when it changes.
       Changes vs original:
         - FAQContentSection import + usage removed (app-only, not needed for editor preview)
         - faqSearchTerms computed removed (now dead after FAQ removal)
         - battlePack prop added (kept for future editor use, not consumed in template)
         - getBattleplanMap imported from @scrolldex/shared (registry-based, 2-arg version)
         - AbilityCard imported from ./AbilityCard.vue (local copy)
         - BattleplanMap imported from ./BattleplanMap.vue (local copy)
         - rulesText rendered above the twist as the general rules body (kept in
           sync with the frontend card) -->

  <div>
    <div class="card-content">
      <div v-if="hasSections" class="battleplan-section-flow">
        <section v-for="(section, index) in battleplan.sections" :key="index" class="rules-section">
          <h3 v-if="section.title" class="rules-section__title">{{ section.title }}</h3>
          <div v-if="section.text" class="rules-section__text" v-html="formatText(section.text)" />
          <div v-if="section.abilities?.length" class="rules-section__abilities">
            <AbilityCard
              v-for="ability in section.abilities"
              :key="ability.name"
              :ability="ability"
            />
          </div>
          <div
            v-if="section.trailingText"
            class="rules-section__text"
            v-html="formatText(section.trailingText)"
          />
        </section>
      </div>

      <template v-if="!hasSections">
        <div
          v-if="battleplan.rulesText"
          class="rules-text"
          v-html="formatText(battleplan.rulesText)"
        />
      </template>

      <div v-if="battleplan.twist" class="twist-text" v-html="formatText(battleplan.twist)" />

      <div
        v-if="battleplan.abilities.length > 0 || battleplan.scoringText"
        class="battleplan-columns"
      >
        <div v-if="battleplan.abilities.length > 0" class="battleplan-col">
          <AbilityCard
            v-for="ability in battleplan.abilities"
            :key="ability.name"
            :ability="ability"
          />
        </div>
        <div v-if="battleplan.scoringText" class="battleplan-col">
          <div class="scoring-text" v-html="formatText(battleplan.scoringText)" />
        </div>
      </div>

      <BattleplanMap v-if="mapData" :map="mapData" />
    </div>
  </div>
</template>

<script setup lang="ts">
// DUPLICATED from packages/frontend for the studio (intentional — see
// docs/superpowers/specs/2026-06-01-battleplan-authoring-tool-design.md §5).
// Keep rendering in sync with the frontend original when it changes.
import { computed } from 'vue';
import type { IBattleplan, BattlePack } from '@scrolldex/shared';
import { getBattleplanMap } from '@scrolldex/shared';
import AbilityCard from './AbilityCard.vue';
import BattleplanMap from './BattleplanMap.vue';
import { formatText } from '../utils/formatter.js';
import { useWorkspace } from '../state/workspace.js';

const props = defineProps<{
  battlePack?: BattlePack;
  battleplan: IBattleplan;
}>();

const ws = useWorkspace();

const hasSections = computed(() => (props.battleplan.sections?.length ?? 0) > 0);

const mapData = computed(() => {
  // Depend on the editor's registry version so the preview map updates live
  // as the map is edited (editor-specific affordance; the app copy reads the
  // registry once). See battleplan-authoring-tool design §5.
  void ws.registryVersion.value;
  return props.battlePack ? getBattleplanMap(props.battlePack, props.battleplan.name) : null;
});
</script>

<style scoped>
/* Studio-elevation preview. A battleplan card raises off the surface as a panel
   with an accent top edge — its identity vs the battle-tactic card's gold header. */
.card-content {
  padding: var(--st-space-4);
  background: var(--st-surface);
  border: 1px solid var(--st-border);
  border-top: 3px solid var(--st-accent);
  border-radius: var(--st-radius-md);
  box-shadow: var(--st-shadow-2);
  margin: var(--st-space-3) 0;
}

.twist-text {
  margin-bottom: var(--st-space-3);
  padding: var(--st-space-2) var(--st-space-3);
  color: var(--st-text);
  line-height: 1.5;
  background: var(--st-accent-tint);
  border-left: 4px solid var(--st-accent);
  border-radius: var(--st-radius-sm);
}

.rules-text {
  margin-bottom: var(--st-space-3);
  color: var(--st-text);
  line-height: 1.5;
  text-align: left;
}

.scoring-text {
  color: var(--st-text);
  line-height: 1.5;
  text-align: left;
}

.battleplan-section-flow {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--st-space-4);
  margin-bottom: var(--st-space-3);
}

.rules-section {
  min-width: 0;
}

.rules-section__title {
  margin: 0 0 var(--st-space-1);
  color: var(--st-text);
  font-size: var(--st-text-lg);
  font-weight: var(--st-weight-bold);
  line-height: 1.1;
  text-transform: uppercase;
}

.rules-section__text {
  color: var(--st-text);
  line-height: 1.5;
  text-align: left;
}

.rules-section__abilities {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-2);
  margin: var(--st-space-2) 0;
}

.battleplan-columns {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-3);
}

.battleplan-col {
  flex: 1;
  min-width: 0;
}

@media (min-width: 700px) {
  .battleplan-columns {
    flex-direction: row;
  }

  .battleplan-section-flow {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
